// src/hooks/useAuctionRosterData.js
// Carica i dati minimi necessari per analizzare scambi in una specifica asta,
// fuori dal contesto di AuctionDetailPage (usato dalla pagina globale /scambi).
// Stessa sequenza di fetch+derivazione già usata in AuctionDetailPage.js, ma
// senza tutto ciò che serve solo al market/svincolati (PMA, categorie, note,
// infortuni): qui bastano rose e ruoli per il motore scambi.
import { useCallback, useEffect, useState } from 'react';
import { ensureFantasyDataSynced, getPlayersFromFirestore, getLineupStatusByPlayerId } from '../firebase/playerService';
import {
  getAuctionById,
  getAuctionPurchases,
  normalizeRosterLimits,
  DEFAULT_ROSTER_LIMITS,
  getAuctionRolesMode,
} from '../firebase/auctionService';
import {
  buildRoleOverrideMap,
  applyRoleOverrideToPlayer,
  applyRoleOverrideToPurchase,
  buildRosterByTeam,
  buildMyRosterPurchases,
} from '../components/auction/auctionRosterUtils';

export default function useAuctionRosterData(ownerUid, auctionId) {
  const [state, setState] = useState({
    loading: true,
    auction: null,
    notFound: false,
    players: [],
    purchases: [],
    rosterByTeam: {},
    myTeamName: '',
    rolesMode: 'classic',
  });

  const load = useCallback(async () => {
    if (!ownerUid || !auctionId) {
      setState((prev) => ({ ...prev, loading: false, notFound: false }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, notFound: false }));

    const auctionData = await getAuctionById(ownerUid, auctionId);
    if (!auctionData) {
      setState({
        loading: false,
        auction: null,
        notFound: true,
        players: [],
        purchases: [],
        rosterByTeam: {},
        myTeamName: '',
        rolesMode: 'classic',
      });
      return;
    }

    let currentPlayers = await getPlayersFromFirestore().catch(() => []);
    if (!currentPlayers.length) {
      await ensureFantasyDataSynced();
      currentPlayers = await getPlayersFromFirestore().catch(() => []);
    }

    const purchaseData = await getAuctionPurchases(ownerUid, auctionId).catch(() => []);
    const roleOverrideMap = buildRoleOverrideMap(auctionData);

    const players = currentPlayers.map((player) => applyRoleOverrideToPlayer(player, roleOverrideMap));

    // Arricchisce ogni acquisto con la probabilità di impiego (prossima
    // giornata, fonte FantaMaster): calcPlayerScore la legge da
    // playerSnapshot.playProbability per pesare leggermente i punteggi usati
    // dal motore scambi verso chi è probabile titolare nel prossimo turno.
    const { byId: playProbabilityById } = await getLineupStatusByPlayerId(currentPlayers).catch(() => ({ byId: {} }));
    const purchases = purchaseData.map((purchase) => {
      const normalized = applyRoleOverrideToPurchase(purchase, roleOverrideMap);
      const entry = playProbabilityById?.[String(normalized.playerId ?? normalized.id)];
      if (!entry) return normalized;
      return {
        ...normalized,
        playerSnapshot: { ...(normalized.playerSnapshot || {}), playProbability: entry },
      };
    });

    const auction = {
      ...auctionData,
      id: auctionId,
      rosterLimits: normalizeRosterLimits(auctionData.rosterLimits || DEFAULT_ROSTER_LIMITS),
    };

    const teamNames = auction.teamNames || [];
    const rosterByTeam = buildRosterByTeam({ teamNames, purchases });
    const myTeamName = auction.myTeamName || teamNames[auction.myTeamIndex || 0] || teamNames[0] || '';

    setState({
      loading: false,
      auction,
      notFound: false,
      players,
      purchases,
      rosterByTeam,
      myTeamName,
      myRosterPurchases: buildMyRosterPurchases({ rosterByTeam, myTeamName }),
      rolesMode: getAuctionRolesMode(auction),
    });
  }, [ownerUid, auctionId]);

  useEffect(() => {
    load().catch(() => setState((prev) => ({ ...prev, loading: false, notFound: true })));
  }, [load]);

  // L'effect gira dopo il paint: se l'asta richiesta è cambiata ma il fetch
  // non ha ancora prodotto un esito, esponi loading=true così il consumatore
  // non mostra per un frame i dati (o l'assenza di dati) della selezione
  // precedente.
  const pending = Boolean(auctionId) && !state.notFound
    && (!state.auction || state.auction.id !== auctionId);

  return { ...state, loading: state.loading || pending };
}
